import { jsPDF } from 'jspdf';
import { byId, fullName, getCourse, getOwner } from './lmsSelectors';

const PAGE_WIDTH = 792;
const PAGE_HEIGHT = 612;

function safeText(value, fallback = '') {
  return String(value || fallback).replace(/[\u2013\u2014]/g, '-');
}

function centerText(doc, text, y, options = {}) {
  const {
    size = 14,
    style = 'normal',
    color = [15, 23, 42],
    maxWidth = 620,
    lineHeight = 22
  } = options;
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const lines = doc.splitTextToSize(safeText(text), maxWidth);
  lines.forEach((line, index) => {
    doc.text(line, PAGE_WIDTH / 2, y + index * lineHeight, { align: 'center' });
  });
  return y + Math.max(lines.length - 1, 0) * lineHeight;
}

function drawCertificateFrame(doc) {
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, 'F');

  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(5);
  doc.rect(32, 32, PAGE_WIDTH - 64, PAGE_HEIGHT - 64, 'S');

  doc.setDrawColor(245, 158, 11);
  doc.setLineWidth(2);
  doc.rect(45, 45, PAGE_WIDTH - 90, PAGE_HEIGHT - 90, 'S');

  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.rect(62, 62, PAGE_WIDTH - 124, PAGE_HEIGHT - 124, 'S');

  doc.setFillColor(239, 246, 255);
  doc.circle(86, 86, 24, 'F');
  doc.circle(PAGE_WIDTH - 86, 86, 24, 'F');
  doc.circle(86, PAGE_HEIGHT - 86, 24, 'F');
  doc.circle(PAGE_WIDTH - 86, PAGE_HEIGHT - 86, 24, 'F');
}

export function downloadCertificatePdf(cert, data) {
  const learner = byId(data.users, cert.userId);
  const course = getCourse(data, cert.courseId);
  const assignment = data.assignments?.find((item) => item.id === cert.assignmentId);
  const issuedDate = new Date(cert.issuedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const completedDate = assignment?.completedAt
    ? new Date(assignment.completedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : issuedDate;
  const issuer = fullName(getOwner(data, course));

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter',
    compress: true
  });

  doc.setProperties({
    title: `${cert.certificateNumber} - Certificate of Completion`,
    subject: safeText(course?.title, 'Course completion'),
    author: safeText(issuer),
    creator: 'Internal LMS'
  });

  drawCertificateFrame(doc);

  centerText(doc, 'Certificate of Completion', 142, {
    size: 38,
    style: 'bold',
    color: [15, 23, 42],
    maxWidth: 660,
    lineHeight: 40
  });

  centerText(doc, 'This certifies that', 220, {
    size: 14,
    color: [71, 85, 105]
  });

  centerText(doc, fullName(learner), 262, {
    size: 30,
    style: 'bold',
    color: [30, 64, 175],
    maxWidth: 660,
    lineHeight: 34
  });

  centerText(doc, 'has successfully completed the internal training course', 316, {
    size: 14,
    color: [71, 85, 105]
  });

  const courseNameY = centerText(doc, course?.title || 'Untitled course', 354, {
    size: 22,
    style: 'bold',
    color: [15, 23, 42],
    maxWidth: 620,
    lineHeight: 26
  });

  centerText(doc, `Completed on ${completedDate}`, Math.max(414, courseNameY + 40), {
    size: 13,
    color: [71, 85, 105]
  });

  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(1);
  doc.line(301, 495, 491, 495);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Authorized Issuer', PAGE_WIDTH / 2, 514, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(safeText(issuer), PAGE_WIDTH / 2, 530, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`Certificate No. ${safeText(cert.certificateNumber)}`, 64, 574);
  doc.text(`Issued ${issuedDate}`, PAGE_WIDTH - 64, 574, { align: 'right' });

  const filename = `${safeText(cert.certificateNumber, 'certificate')}.pdf`;
  doc.save(filename);
}
